import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext } from "react-beautiful-dnd";
import { Box, Button, Stack, FormControlLabel, Switch, Container } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { skipToken } from "@reduxjs/toolkit/dist/query";

// Cast RBD components to fix React 18 type incompatibility
const DragDropContextComp = DragDropContext as any;

import PageHeader from "@/components/PageHeader";
import { useFetchMenuByIdQuery, useUpdateMenuMutation } from "@/features/organizations/api/menusApi";
import { useFetchContentQuery } from "@/features/organizations/api/contentApi";
import { ContentStatus } from "@/types";
import useSnackbar from "@/hooks/useSnackbar";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import { setContainer } from "@/store/uiSlice";

import useCatalogEditor from "./hooks/useCatalogEditor";
import ProductsColumn from "./components/ProductsColumn";
import SectionsColumn from "./components/SectionsColumn";
import ContentDrawer from "../organization-content/Drawer";
import TabsColumn from "./components/TabsColumn";
import CatalogImportWizard from "./components/CatalogImportWizard";
import CatalogEditorMobile from "./components/CatalogEditorMobile";

const CatalogEditor = () => {
  const { organizationId = "", menuId = "" } = useParams<{
    organizationId: string;
    menuId: string;
  }>();
  const container = useAppSelector((state) => state.ui?.container);
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [isPublished, setIsPublished] = useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [selectedSectionCategory, setSelectedSectionCategory] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"steps" | "options">("steps");
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { isMobile } = useResponsive();

  useEffect(() => {
    dispatch(setContainer("fluid"));
    return () => {
      dispatch(setContainer("fixed"));
    };
  }, [dispatch]);

  // ── Data fetching ────────────────────────────────────────────────────────────
  const menuQuery = useFetchMenuByIdQuery(
    { menuId, organizationId },
    { skip: !menuId || !organizationId }
  );

  const contentQuery = useFetchContentQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });

  const [updateMenu, { isLoading: isSaving }] = useUpdateMenuMutation();

  // Published products only
  const allContent = (contentQuery.data ?? []).filter(
    (c: import("@/types").Content) => c.status === ContentStatus.PUBLISHED && c.type !== "offer"
  );

  // ── Editor state & DnD logic (custom hook) ──────────────────────────────────
  const {
    structure,
    setStructure,
    filteredProducts,
    searchQuery,
    setSearchQuery,
    addSection,
    removeSection,
    updateSection,
    removeProductFromSection,
    onDragEnd,
  } = useCatalogEditor({ initialStructure: [], allContent });

  // Hydrate structure from Firestore once the menu loads
  useEffect(() => {
    if (menuQuery.data?.structure) {
      setStructure(menuQuery.data.structure);
    }
  }, [menuQuery.data?.structure]);

  // Hydrate publication state once the menu loads
  useEffect(() => {
    if (menuQuery.data) {
      setIsPublished(!!menuQuery.data.isPublished);
    }
  }, [menuQuery.data]);

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!menuQuery.data) return;

    const response = await updateMenu({
      menu: { ...menuQuery.data, structure, isPublished },
      organizationId,
    });

    if (response?.error) {
      openSnackbar("Error al guardar el catálogo", "error");
    } else {
      openSnackbar("Catálogo guardado", "success");
    }
  };

  // ── Section expand/collapse ───────────────────────────────────────────────────
  const handleToggleExpand = (category: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: prev[category] === false ? true : false,
    }));
  };

  const handleCollapseAll = () => {
    const areAllCollapsed = structure.every(
      (section) => expandedSections[section.category] === false
    );
    const nextState = areAllCollapsed;
    const newMap: Record<string, boolean> = {};
    structure.forEach((section) => {
      newMap[section.category] = nextState;
    });
    setExpandedSections(newMap);
  };

  // ── Header actions ───────────────────────────────────────────────────────────
  const headerAction = (
    <Stack direction="row" spacing={2} alignItems="center">
      <FormControlLabel
        control={
          <Switch
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
          />
        }
        label="Publicado"
      />
      <Button variant="outlined" onClick={() => navigate(-1)}>
        Cancelar
      </Button>
      <LoadingButton
        variant="contained"
        color="success"
        loading={isSaving}
        onClick={handleSave}
      >
        Guardar
      </LoadingButton>
    </Stack>
  );

  const handleDragEnd = (result: any) => {
    onDragEnd(result);
    const { destination } = result;
    if (destination) {
      const dstId = destination.droppableId;
      const targetSection = structure.find((s) => s.category === dstId);
      if (targetSection) {
        setSelectedSectionCategory(targetSection.category);
        setActiveTab("options");
      }
    }
  };

  if (isMobile) {
    return (
      <Stack id="catalog-editor-container" spacing={2} sx={{ height: "100%", overflow: "hidden", p: 1 }}>
        <CatalogEditorMobile
          structure={structure}
          setStructure={setStructure}
          allContent={allContent}
          filteredProducts={filteredProducts}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          addSection={addSection}
          removeSection={removeSection}
          updateSection={updateSection}
          removeProductFromSection={removeProductFromSection}
          isSaving={isSaving}
          isPublished={isPublished}
          setIsPublished={setIsPublished}
          handleSave={handleSave}
          organizationId={organizationId}
          menuId={menuId}
          onCreateProductClick={() => setIsCreateDrawerOpen(true)}
          onImportProductClick={() => setIsImportWizardOpen(true)}
        />

        <ContentDrawer
          open={isCreateDrawerOpen}
          onClose={() => setIsCreateDrawerOpen(false)}
        />

        <CatalogImportWizard
          open={isImportWizardOpen}
          onClose={() => setIsImportWizardOpen(false)}
          organizationId={organizationId}
          catalogId={menuId}
        />
      </Stack>
    );
  }

  return (
    <Stack id="catalog-editor-container" spacing={2} sx={{ height: "100%", overflow: "hidden" }}>
      <PageHeader
        title={menuQuery.data?.title ?? "Editor de Catálogo"}
        action={headerAction}
      />
      <Stack>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >

          {/* Three-column editor with live preview in right column */}
          <DragDropContextComp onDragEnd={handleDragEnd}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "3fr 6fr 3fr" },
                gap: 2,
                height: "calc(100vh - 130px)",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {/* Left: Products catalog */}
              <ProductsColumn
                products={filteredProducts}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={contentQuery.isLoading}
                onCreateProductClick={() => setIsCreateDrawerOpen(true)}
                onImportProductClick={() => setIsImportWizardOpen(true)}
              />

              {/* Middle: Sections */}
              <SectionsColumn
                structure={structure}
                allContent={allContent}
                expandedSections={expandedSections}
                onToggleExpand={handleToggleExpand}
                onAddSection={() => {
                  addSection();
                }}
                onCollapseAll={handleCollapseAll}
                onUpdateSection={updateSection}
                onRemoveSection={(idx) => {
                  const sectionToRemove = structure[idx];
                  if (sectionToRemove && selectedSectionCategory === sectionToRemove.category) {
                    setSelectedSectionCategory(null);
                  }
                  removeSection(idx);
                }}
                onRemoveProduct={removeProductFromSection}
                selectedSectionCategory={selectedSectionCategory}
                onSelectSection={(category) => {
                  setSelectedSectionCategory(category);
                  setActiveTab("options");
                }}
              />

              {/* Right: Section Settings Tab */}
              <TabsColumn
                structure={structure}
                allContent={allContent}
                onAddSection={addSection}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                selectedSection={
                  selectedSectionCategory
                    ? structure.find((s) => s.category === selectedSectionCategory) ?? null
                    : null
                }
                onUpdateSection={(patch) => {
                  const idx = structure.findIndex((s) => s.category === selectedSectionCategory);
                  if (idx !== -1) {
                    updateSection(idx, patch);
                  }
                }}
              />
            </Box>
          </DragDropContextComp>

          <ContentDrawer
            open={isCreateDrawerOpen}
            onClose={() => setIsCreateDrawerOpen(false)}
          />

          <CatalogImportWizard
            open={isImportWizardOpen}
            onClose={() => setIsImportWizardOpen(false)}
            organizationId={organizationId}
            catalogId={menuId}
          />
        </Container>
      </Stack>
    </Stack>
  );
};

export default CatalogEditor;
